import React from "react";
import {MediaFilesList} from "../../models/mediaFiles/MediaFilesListModel";
import {MediaFilesListViewState} from "../../components/mediaFiles/list/MediaFilesListViewState";
import async from "../../components/Async";
import IosPaper from "react-ionicons/lib/IosPaper";
import {buildMediaFilesColumns} from "../../components/mediaFiles/list/MediaFilesListView";
import {ModalState} from "../../components/elements/modal/GenericModal";
import {getEnv} from "mobx-state-tree";
import Parameter from "../../models/Parameter";
import MediaFileStore from "../../stores/domain/MediaFileStore";
import {RouterState, RouterStore} from "mobx-state-router";
import {CreateUtmState} from "../../components/utms/create/CreateUtmState";
import {Utm} from "../../models/utms/UtmModel";
import CreateMediaFileHome from "../../pages/mediaFiles/CreateMediaFileHome";
import {CreateMediaFileState} from "../../components/mediaFiles/create/CreateMediaFileState";
import EditMediaFileHome from "../../pages/mediaFiles/EditMediaFileHome";
import {MediaFile} from "../../models/mediaFiles/MediaFileModel";
import {EditMediaFileState} from "../../components/mediaFiles/edit/EditMediaFileState";
import {checkUserPermissions} from "../../utils/requiredRolesCheck";
import {isEpsFormat} from "../../utils/common";

// Page Async Reference, for lazy loading in UI
const MediaFilesListPage = async(()=> import("../../pages/mediaFiles/MediaFilesListHome"));
export const viewMap = {
    mediaFiles: <MediaFilesListPage/>,
    createMediaFile: <CreateMediaFileHome/>,
    editMediaFile: <EditMediaFileHome/>

};
export const routeDefinitions = [
  {
    path: "mediaFiles",
    name: "Media Files",
    icon: IosPaper,
    component: MediaFilesListPage,
    children: null,
    featureFlagRequired: "cmp_ui_media_files"
  }
];
export const routeTransitions = [
  {
    name: "mediaFiles",
    pattern: "/mediaFiles",
    onEnter: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      const groupSelectState = rootStore.rootUiStore.groupSelectStore;
      const tagSelectStore = rootStore.rootUiStore.tagSelectStore;
      tagSelectStore?.tagStore?.init(); //reset tags
      tagSelectStore?.setSelectedTags([]);
      const mediaFileStore = rootStore.domainStore.mediaFileStore;

      // Check if mediaFilesListViewState already exists, only create if it doesn't
      if (!rootStore.rootUiStore.mediaFilesListViewState) {
        const mediaFilesList = MediaFilesList.create({
          store:  mediaFileStore
        });
        const advFilterSelectState = rootStore.rootUiStore.advFilterSelectState;
        const codeOwnerFilterSelectState = rootStore.rootUiStore.codeOwnerFilterSelectState;
        const dimensionSelectViewState = rootStore.rootUiStore.dimensionSelectViewState;
        const tagFilterSelectState = rootStore.rootUiStore.tagFilterSelectState;
        const groupFilterSelectState = rootStore.rootUiStore.groupFilterSelectState;
        advFilterSelectState.reset();
        advFilterSelectState.setEnableMediaOptions();
        codeOwnerFilterSelectState.reset();
        dimensionSelectViewState.reset();
        tagFilterSelectState.reset();
        groupFilterSelectState.reset();
        const modalState = ModalState.create({});
        const mediaFilesListViewState = MediaFilesListViewState.create({
          listModel: mediaFilesList,
          groupSelectStore: groupSelectState,
          tagSelectStore:tagSelectStore,
          modalStore: modalState,
          advFilterSelectState: advFilterSelectState,
          codeOwnerFilterSelectState: codeOwnerFilterSelectState,
          dimensionSelectViewState: dimensionSelectViewState,
          tagFilterSelectState: tagFilterSelectState,
          groupFilterSelectState: groupFilterSelectState
        });

        mediaFilesListViewState.loadColumnsFromSession();
        mediaFilesListViewState.setColumns(buildMediaFilesColumns(routerStore, mediaFilesListViewState))
        await rootStore.rootUiStore.attachPageState("mediaFilesListViewState", mediaFilesListViewState);
      }

      // Always refresh the data when entering the route
      rootStore.rootUiStore.mediaFilesListViewState?.refresh();
      await rootStore.rootUiStore.mediaFilesListViewState?.getFilters();
    },
    onExit: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      const advFilterSelectState = rootStore.rootUiStore.advFilterSelectState;
      advFilterSelectState?.clearEnableMediaOptions();
    }
  },
  {
    name: 'createMediaFile',
    pattern: '/configs/mediaFiles/create',
    beforeEnter: checkUserPermissions,
    onEnter: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      let {queryParams} = toState;
      const groupSelectStore = rootStore.rootUiStore.groupSelectStore;

      const tagSelectStore = rootStore.rootUiStore.tagSelectStore;
      const dimensionsMultiSelectState = rootStore.rootUiStore.dimensionsMultiSelectState;

        groupSelectStore.selectGroup(groupSelectStore.groupStore?.groups[0]);
        tagSelectStore?.tagStore?.init(); //reset tags
        tagSelectStore?.setSelectedTags([]);
        dimensionsMultiSelectState.init();
        dimensionsMultiSelectState.setDefaults();
        dimensionsMultiSelectState.getListOnGroup(groupSelectStore.selectedGroup);
        const createMediaFileState = CreateMediaFileState.create({
          mediaFile: MediaFile.create({uuid:"createMediaFile"}),
          groupSelectStore:groupSelectStore,
          tagSelectStore:tagSelectStore,
          dimensionsMultiSelectStore: dimensionsMultiSelectState,
          codeTypeSelectStore: rootStore.rootUiStore.codeTypeSelectStore
        })
      await rootStore.rootUiStore.attachPageState('createMediaFileState', createMediaFileState);
    },
    onExit: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
    }
  },
  {
    name: 'editMediaFile',
    pattern: '/configs/mediaFiles/:uuid/edit',
    beforeEnter: checkUserPermissions,
    onEnter: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      const {uuid} = toState.params;
      const mediaFileStore = rootStore.domainStore.mediaFileStore;
      const file = await mediaFileStore.getMediaFile(uuid);
      const groupSelectStore = rootStore.rootUiStore.groupSelectStore;
      const tagSelectStore = rootStore.rootUiStore.tagSelectStore;
      const dimensionsMultiSelectState = rootStore.rootUiStore.dimensionsMultiSelectState;
      dimensionsMultiSelectState.init();
      dimensionsMultiSelectState.setDefaults();
      const codeTypeSelectStore = rootStore.rootUiStore.codeTypeSelectStore;
      const editMediaFileState = EditMediaFileState.create({
        mediaFile: MediaFile.create({
          uuid:uuid,
          status: file?.fileLocation.status || "",
          name:file?.fileLocation.name || "",
          usage:file?.fileLocation.usage || "",
          fileFormat:file?.fileLocation.fileFormat || "",
          mediaType:file?.fileLocation.mediaType || "",
          liveLinks: {...file?.fileLocation?.liveLinks} || [],
          codeUUIDs: [...file?.fileLocation?.codeUUIDs] || [],
        }),
        groupSelectStore: groupSelectStore,
        tagSelectStore: tagSelectStore,
        dimensionsMultiSelectStore: dimensionsMultiSelectState,
        codeTypeSelectStore: codeTypeSelectStore
      });
      await rootStore.rootUiStore.attachPageState('editMediaFileState', editMediaFileState);
      const isEps = isEpsFormat({
        name: file.fileLocation.name,
        type: file.fileLocation.fileFormat
      });
      if (isEps){
        try {
          const epsUrl = file?.fileLocation.liveLinks?.original || "";
          const base64SrcImg = await getEnv(rootStore).mediaFileManager.convertEpsToBase64(epsUrl);

          editMediaFileState.setBase64NonBrowserConvertedFile(base64SrcImg);// set the img tag here
        } catch (error) {
          console.error("Failed to convert EPS file:", error.message);
          editMediaFileState.setBase64NonBrowserConvertedFile("");
          // Optionally show user-friendly error message
          // You might want to set an error state or show a notification
        }
      } else {
        // handle other formats as before
      }
      editMediaFileState.setWaitingForMediaFileUpload(false)
      editMediaFileState.hydrateMediaFileForm(file?.fileLocation);

    },
    onExit: async (
      fromState: RouterState,
      toState: RouterState,
      routerStore: RouterStore
    ) => {
      const {rootStore} = routerStore.options;
      console.log("exit", fromState)
      console.log("exit", toState)
      console.log("exit", routerStore)
      const editMediaFileState = rootStore.rootUiStore.editMediaFileState
      editMediaFileState?.setCancelUploads(true);
      editMediaFileState?.removeMediaFile();

    }
  }
]
